/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import type {
  Error,
  GeneratePasswordRequest,
  GeneratePasswordResponse,
  PasswordStrengthRequest,
  PasswordStrengthResponse,
  UserPasswordStrengthResponse,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Evaluates a password and returns a strength score and whether it meets platform requirements. The password is not stored.
 * @summary Check password strength
 */
export const checkPasswordStrength = (
  passwordStrengthRequest: PasswordStrengthRequest,
  signal?: AbortSignal
) => {
  return customFetch<PasswordStrengthResponse>({
    url: `/v1/password_strength`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: passwordStrengthRequest,
    signal,
  });
};

export const getCheckPasswordStrengthMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkPasswordStrength>>,
    TError,
    { data: PasswordStrengthRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof checkPasswordStrength>>,
  TError,
  { data: PasswordStrengthRequest },
  TContext
> => {
  const mutationKey = ['checkPasswordStrength'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof checkPasswordStrength>>,
    { data: PasswordStrengthRequest }
  > = props => {
    const { data } = props ?? {};

    return checkPasswordStrength(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/password_strength` },
    {
      operationId: 'app.controllers.password_protection_controller.check_password_strength',
      operationName: 'checkPasswordStrength',
    }
  );

  return customOptions;
};

export type CheckPasswordStrengthMutationResult = NonNullable<
  Awaited<ReturnType<typeof checkPasswordStrength>>
>;
export type CheckPasswordStrengthMutationBody = PasswordStrengthRequest;
export type CheckPasswordStrengthMutationError = Error;

/**
 * @summary Check password strength
 */
export const useCheckPasswordStrength = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof checkPasswordStrength>>,
      TError,
      { data: PasswordStrengthRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof checkPasswordStrength>>,
  TError,
  { data: PasswordStrengthRequest },
  TContext
> => {
  const mutationOptions = getCheckPasswordStrengthMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a random password that meets platform rules. Optional length from 16 to 72 characters.
 * @summary Generate a strong password
 */
export const generateAccountUserRandomPassword = (
  generatePasswordRequest?: GeneratePasswordRequest,
  signal?: AbortSignal
) => {
  return customFetch<GeneratePasswordResponse>({
    url: `/v1/users/generate_password`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: generatePasswordRequest,
    signal,
  });
};

export const getGenerateAccountUserRandomPasswordMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateAccountUserRandomPassword>>,
    TError,
    { data: GeneratePasswordRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof generateAccountUserRandomPassword>>,
  TError,
  { data: GeneratePasswordRequest },
  TContext
> => {
  const mutationKey = ['generateAccountUserRandomPassword'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateAccountUserRandomPassword>>,
    { data: GeneratePasswordRequest }
  > = props => {
    const { data } = props ?? {};

    return generateAccountUserRandomPassword(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/users/generate_password` },
    {
      operationId: 'app.controllers.account_users_controller.generate_account_user_random_password',
      operationName: 'generateAccountUserRandomPassword',
    }
  );

  return customOptions;
};

export type GenerateAccountUserRandomPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateAccountUserRandomPassword>>
>;
export type GenerateAccountUserRandomPasswordMutationBody = GeneratePasswordRequest;
export type GenerateAccountUserRandomPasswordMutationError = Error;

/**
 * @summary Generate a strong password
 */
export const useGenerateAccountUserRandomPassword = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateAccountUserRandomPassword>>,
      TError,
      { data: GeneratePasswordRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof generateAccountUserRandomPassword>>,
  TError,
  { data: GeneratePasswordRequest },
  TContext
> => {
  const mutationOptions = getGenerateAccountUserRandomPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Tests whether a password meets Rocket.net requirements. The password is not stored.
 * @summary Check password strength
 */
export const checkAccountUsersPasswordStrength = (
  passwordStrengthRequest: PasswordStrengthRequest,
  signal?: AbortSignal
) => {
  return customFetch<UserPasswordStrengthResponse>({
    url: `/v1/users/password_strength`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: passwordStrengthRequest,
    signal,
  });
};

export const getCheckAccountUsersPasswordStrengthMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkAccountUsersPasswordStrength>>,
    TError,
    { data: PasswordStrengthRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof checkAccountUsersPasswordStrength>>,
  TError,
  { data: PasswordStrengthRequest },
  TContext
> => {
  const mutationKey = ['checkAccountUsersPasswordStrength'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof checkAccountUsersPasswordStrength>>,
    { data: PasswordStrengthRequest }
  > = props => {
    const { data } = props ?? {};

    return checkAccountUsersPasswordStrength(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/users/password_strength` },
    {
      operationId: 'app.controllers.account_users_controller.check_account_users_password_strength',
      operationName: 'checkAccountUsersPasswordStrength',
    }
  );

  return customOptions;
};

export type CheckAccountUsersPasswordStrengthMutationResult = NonNullable<
  Awaited<ReturnType<typeof checkAccountUsersPasswordStrength>>
>;
export type CheckAccountUsersPasswordStrengthMutationBody = PasswordStrengthRequest;
export type CheckAccountUsersPasswordStrengthMutationError = Error;

/**
 * @summary Check password strength
 */
export const useCheckAccountUsersPasswordStrength = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof checkAccountUsersPasswordStrength>>,
      TError,
      { data: PasswordStrengthRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof checkAccountUsersPasswordStrength>>,
  TError,
  { data: PasswordStrengthRequest },
  TContext
> => {
  const mutationOptions = getCheckAccountUsersPasswordStrengthMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
